import { useState } from "react";
import { X } from "lucide-react";
import type { InSnipCard } from "./types";

/** A daily news-quiz question stored in the quiz card's payload. */
type Question = { q: string; options: string[]; answer: number; why?: string };

/**
 * Interactive daily quiz: one question at a time, instant right/wrong feedback with the
 * explanation, then a final score the reader can share ("I got 4/5"). Self-contained -
 * the only inputs are the quiz card and two callbacks.
 */
export function QuizModal(props: {
  quiz: InSnipCard;
  onClose: () => void;
  /** results carries one boolean per question, in answer order, for the share grid. */
  onShare: (score: number, total: number, results: boolean[]) => void;
}) {
  const questions = (
    ((props.quiz.payload as { questions?: Question[] } | undefined)?.questions ?? []) as Question[]
  ).filter((q) => q && Array.isArray(q.options) && q.options.length >= 2);

  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState<number | null>(null);
  const [score, setScore] = useState(0);
  const [results, setResults] = useState<boolean[]>([]);
  const [done, setDone] = useState(false);
  const total = questions.length;

  function pick(i: number) {
    if (picked !== null) return;
    setPicked(i);
    const right = i === questions[idx].answer;
    if (right) setScore((s) => s + 1);
    setResults((r) => [...r, right]);
  }
  function next() {
    if (idx + 1 < total) {
      setIdx(idx + 1);
      setPicked(null);
    } else {
      setDone(true);
    }
  }

  const verdict =
    score === total
      ? "Perfect, you didn't miss a beat today."
      : score >= Math.ceil(total * 0.6)
        ? "Sharp, you've been paying attention."
        : "A few got you. Come back tomorrow and beat it.";

  return (
    <section
      className="insight-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Daily quiz"
      onClick={props.onClose}
    >
      <div className="quiz-modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="quiz-close icon-button ghost"
          onClick={props.onClose}
          aria-label="Close quiz"
        >
          <X size={18} />
        </button>

        {total === 0 ? (
          <div className="quiz-result">
            <div className="quiz-result-emoji" aria-hidden="true">📰</div>
            <h2>No quiz yet today</h2>
            <p>Today's quiz lands with the morning drop. Check back soon.</p>
            <button type="button" className="ghost-action" onClick={props.onClose}>Close</button>
          </div>
        ) : done ? (
          <div className="quiz-result">
            <div className="quiz-result-emoji" aria-hidden="true">
              {score === total ? "🏆" : score >= Math.ceil(total * 0.6) ? "🎉" : "📰"}
            </div>
            <span className="quiz-eyebrow">Today's quiz</span>
            <h2>You scored {score}/{total}</h2>
            <p>{verdict}</p>
            <div className="quiz-actions">
              <button type="button" className="quiz-share" onClick={() => props.onShare(score, total, results)}>
                Share my result
              </button>
              <button type="button" className="ghost-action" onClick={props.onClose}>Done</button>
            </div>
          </div>
        ) : (
          <>
            <div className="quiz-progress">
              <span>Question {idx + 1} of {total}</span>
              <span className="quiz-score">{score} correct</span>
            </div>
            <div className="quiz-bar" aria-hidden="true">
              <span style={{ width: `${(idx / total) * 100}%` }} />
            </div>
            <h2 className="quiz-q">{questions[idx].q}</h2>
            <div className="quiz-options">
              {questions[idx].options.map((opt, i) => {
                const state =
                  picked === null
                    ? ""
                    : i === questions[idx].answer
                      ? " correct"
                      : i === picked
                        ? " wrong"
                        : " dim";
                return (
                  <button
                    key={i}
                    type="button"
                    className={`quiz-option${state}`}
                    onClick={() => pick(i)}
                    disabled={picked !== null}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
            {picked !== null ? (
              <>
                {questions[idx].why ? (
                  <p className="quiz-why">
                    {picked === questions[idx].answer ? "✓ " : "✗ "}
                    {questions[idx].why}
                  </p>
                ) : null}
                <button type="button" className="quiz-next" onClick={next}>
                  {idx + 1 < total ? "Next question →" : "See my score"}
                </button>
              </>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}
